import { useMemo } from 'react';

import { usePanelNavigation, usePanelScopeRegistry } from '@kc-console/context';

import PanelTabHost, { usePanelTabHostAvailable } from '../components/PageLayout/PanelTabHost';
import { PanelErrorBoundary } from '../components/PanelErrorBoundary';

import type { ReactElement } from 'react';
import type { PanelId, PanelInfo, PanelState } from '@kc-console/context';

// 경고 중복 출력 방어: 이미 경고를 출력한 scope를 추적
const warnedScopes = new Set<string>();

/**
 * panelId를 PanelErrorBoundary의 resetKey용 안정 문자열로 직렬화합니다.
 * @description
 * - panelId는 다중 선택 패널에서 배열일 수 있어 `String(['a','b'])`는 `"a,b"`가 되고
 *   값에 콤마가 포함되면 항목 경계가 모호해집니다. 콤마와 충돌이 적은 구분자로 join해
 *   항목 구성이 바뀌면 resetKey도 바뀌도록(=boundary 복구) 보장합니다.
 */
const toResetKey = (panelId: PanelId): string =>
  Array.isArray(panelId) ? panelId.join('|') : String(panelId);

/**
 * PanelViewport 상태 관리 훅
 *
 * - PanelNavigation 상태를 직접 구독
 * - visible/targetPanel 파생값 계산
 * - selectedItems 목록 생성
 *
 * @example
 * ```tsx
 * const { visible, targetPanel, selectedItems } = usePanelViewport();
 *
 * if (!visible) return null;
 * return <PanelPortal>{targetPanel}</PanelPortal>;
 * ```
 */
export function usePanelViewport() {
  const panels = usePanelNavigation(state => state.panels);
  const closePanel = usePanelNavigation(state => state.closePanel);
  const getScopeConfig = usePanelScopeRegistry(state => state.getScopeConfig);

  // standalone 패널 필터링 (list/detail 모두 포함)
  const standalonePanels = useMemo(() => {
    return Array.from(panels.entries()).filter(
      (entry): entry is [string, PanelState & { panel: PanelInfo & { panelId: PanelId } }] => {
        const [, panelState] = entry;
        const { panel } = panelState;

        // Phase 2: type 가드 해제
        // - list/detail 모두 렌더링 가능
        // - panelId가 없으면 런타임 위험 방지
        return panel.viewMode === 'standalone' && panel.panelId !== undefined;
      },
    );
  }, [panels]);

  // 패널 컴포넌트 렌더 노드 생성
  const panelNodes = useMemo(() => {
    return standalonePanels
      .map(([scopeKey, panelState]) => {
        const config = getScopeConfig(scopeKey);
        // Phase 5: panelComponent가 Record 타입이므로 default 조회
        const PanelComponent = config?.panelComponent?.default;

        // 매핑되지 않은 scope는 경고 로그 (개발 모드에서만, 중복 방지)
        if (!PanelComponent) {
          if (process.env.NODE_ENV === 'development' && !warnedScopes.has(scopeKey)) {
            console.warn(`[usePanelViewport] No component mapped for scope: ${scopeKey}`);
            warnedScopes.add(scopeKey);
          }
          return null;
        }

        // 타입 가드로 panelId 존재 보장됨
        // 패널 컨텐츠 throw(DetailContentGate의 게이팅 에러 등)를 boundary가 잡아 인플레이스 에러 화면으로 교체한다.
        // resetKey(panelId)가 바뀌면 boundary가 복구되어 다른 항목 선택 시 정상 렌더를 재시도한다.
        return (
          <PanelErrorBoundary
            key={scopeKey}
            resetKey={toResetKey(panelState.panel.panelId)}
            onClose={() => closePanel(scopeKey)}
          >
            <PanelComponent
              panelId={panelState.panel.panelId}
              selection={panelState.selection}
              scopeKey={scopeKey}
              onClose={() => closePanel(scopeKey)}
            />
          </PanelErrorBoundary>
        );
      })
      .filter((node): node is ReactElement => Boolean(node));
  }, [closePanel, getScopeConfig, standalonePanels]);

  // 선택된 아이템 목록
  const selectedItems = useMemo(() => {
    return standalonePanels.flatMap(([, panelState]) => {
      const selectionIds = panelState.selection?.ids;
      const ids = selectionIds?.length
        ? selectionIds
        : Array.isArray(panelState.panel.panelId)
          ? panelState.panel.panelId
          : panelState.panel.panelId
            ? [panelState.panel.panelId]
            : [];

      return ids.map(id => ({ id }));
    });
  }, [standalonePanels]);

  const hasTabHost = usePanelTabHostAvailable();

  // 가시성: 패널 노드가 있거나 탭 호스트가 사용 가능하면 표시
  const visible = panelNodes.length > 0 || hasTabHost;

  const targetPanel = useMemo(() => {
    // 탭 호스트 우선 (탭이 있을 때)
    if (hasTabHost) {
      return (
        <PanelTabHost
          onClosePanel={() => {
            // 모든 패널 닫기
            standalonePanels.forEach(([scopeKey]) => {
              closePanel(scopeKey);
            });
          }}
        />
      );
    }

    // 기존 패널 노드 렌더링
    if (panelNodes.length > 0) {
      return <>{panelNodes}</>;
    }

    return null;
  }, [hasTabHost, panelNodes, standalonePanels, closePanel]);

  return {
    /** 패널 표시 여부 */
    visible,
    /** 렌더링할 패널 컴포넌트 */
    targetPanel,
    /** 선택된 아이템 목록 */
    selectedItems,
    /** 개별 패널 노드 배열 */
    panelNodes,
    /** 필터링된 standalone 패널 목록 */
    standalonePanels,
    /** 탭 호스트 사용 가능 여부 */
    hasTabHost,
  };
}
